import React, { useMemo, useState } from 'react';
import Icon from '../../shared/Icon';
import { UseProposalWorkspaceResult } from '../../../hooks/useProposalWorkspace';
import { WorkspaceActions } from '../ProposalWorkspace';
import { SURFACE_CENTRAL, TONE_BADGE } from '../../../utils/statusColors';
import { canSubmitProposal, SENT_PROPOSAL_STATUSES, isDeadlinePassed, isJobRunning } from '../../../utils/proposalStage';
import { fmtDateTime } from '../../../utils/formatters';
import { procurementService } from '../../../services/api';
import ProcessDocumentsCard from '../ProcessDocumentsCard';
import { estadoDaChecagem } from '../../../utils/checagemDoEnvio';
import type { Carga } from '../../../design';
import { campo } from '../../../design';
import { FRASE_SEM_ENVIO_PELO_HUB } from '../portais/capacidades';
import type { EnvioPeloHub } from '../portais/capacidades';
import GravarItensNoPortal from './GravarItensNoPortal';
import { propostaEnviadaPeloHub } from '../../../utils/gravacaoNoPortal';
import type { GravacaoNoPortal } from '../../../hooks/useGravacaoNoPortal';
import { ehSimulacao, JobDaLista, quandoDoJob } from '../../../utils/jobDeAutomacao';

/**
 * Estágio ENVIO — a antessala do portal.
 *
 * Reúne o que estava espalhado: os termos ficavam numa aba irmã de "Itens"
 * (como se aceitar declaração fosse parte de montar preço), os documentos
 * exigidos do BLL num bloco solto, e o botão Enviar existia em três lugares
 * com regras diferentes — dizendo "não pode" sem dizer por quê.
 *
 * Aqui: primeiro a CHECAGEM (o que impede o envio, item a item), depois os
 * termos do portal daquela licitação, depois os documentos exigidos, e só
 * então o envio.
 */

const JOB_BADGE: Record<string, string> = {
  queued: TONE_BADGE.warning, running: TONE_BADGE.live, completed: TONE_BADGE.success,
  failed: TONE_BADGE.danger, needs_manual: TONE_BADGE.warning,
};
const JOB_LABEL: Record<string, string> = {
  queued: 'na fila', running: 'executando', completed: 'concluído',
  failed: 'falhou', needs_manual: 'ação manual',
};

interface Props {
  ws: UseProposalWorkspaceResult;
  actions: WorkspaceActions;
  /** Da LISTA de jobs (formato enxuto): ver utils/jobDeAutomacao. */
  latestJob?: JobDaLista | null;
  jobs?: JobDaLista[];
  /** O estado da carga do histórico de envios (os jobs). Sem ele conhecido, a checagem não fica verde. */
  historicoDeEnvio?: Pick<Carga, 'estado' | 'erro'> | null;
  /** O portal do item tem envio pelo HUB (`envioPeloHub` da matriz)? 'nao' troca o envio pelo "Já enviei manualmente". */
  envioPeloHub?: EnvioPeloHub;
  /** No modo `grava_itens`: o estado da gravação (o mesmo que o botão do topo lê). */
  gravacao?: GravacaoNoPortal | null;
}

export default function EnvioSection({ ws, actions, latestJob, jobs = [], historicoDeEnvio = null, envioPeloHub = 'desconhecido', gravacao = null }: Props) {
  // Portal sem envio pelo HUB (a etapa `cadastrar` da matriz): o "Enviar proposta" levava à recusa do backend
  // (SAAE Ituiutaba, Licitanet, 18/10/2026). Aqui o caminho é o portal + "Já enviei manualmente".
  // O modo que só grava (Licitanet): enviada pelo Heitor no portal, também não há "Reenviar" pelo HUB.
  const gravaItens = envioPeloHub === 'grava_itens';
  const semEnvio = envioPeloHub === 'nao' || gravaItens;
  const { proposal, item, declarations, setDeclarations, checklist, checklistLoading } = ws;
  const portal = String(item?.portalOrigem || '').toLowerCase();
  const td: any = item?.termsDeclarations || null;
  const [uploading, setUploading] = useState<string | null>(null);
  const [showTermsDetails, setShowTermsDetails] = useState(false);

  // Declarações que o Termo de Aceitação engloba, agrupadas como o portal exibe
  // (condições de participação / habilitação / trabalhista / cooperativa). Vêm do
  // scrape em termsDeclarations.termoAceitacao.declaracoes. O workspace mostrava
  // só o enunciado genérico e o usuário assinava sem ver O QUE estava aceitando.
  const declGrupos: Array<[string, string[]]> = Object.entries(
    (td?.termoAceitacao?.declaracoes || {}) as Record<string, string[]>,
  ).filter(([, arr]) => Array.isArray(arr) && arr.length > 0);
  const totalDeclaracoes = declGrupos.reduce((s, [, arr]) => s + arr.length, 0);

  // Documentos obrigatórios do processo entram na CHECAGEM. Antes não entravam:
  // a tela dizia "✓ Tudo certo para enviar" e, três linhas abaixo, "0/2
  // anexados · 1 obrigatório". O impedimento só aparecia como toast, depois do
  // clique em Enviar (auditoria 25/08/2026). A conta é do hook, não daqui, pra
  // o botão laranja do cabeçalho responder a mesma coisa.
  const docsCheck = ws.docsCheck;

  const check = useMemo(
    () => canSubmitProposal({ proposal: { ...proposal, proposalItems: ws.items }, item, latestJob, docsCheck }),
    [proposal, ws.items, item, latestJob, docsCheck],
  );
  // Três estados (07/10/2026): a checagem também olha a última tentativa de envio. Ver utils/checagemDoEnvio.
  const checagem = useMemo(
    () => estadoDaChecagem({ check, jobs, proposal, historico: historicoDeEnvio }),
    [check, jobs, proposal, historicoDeEnvio],
  );
  // O verde diz O QUE foi conferido, não que o portal vai aceitar.
  const conferido = ['itens com preço', 'prazo aberto', 'nenhum bloqueio registrado pelo portal']
    .concat(docsCheck ? ['documentos obrigatórios do processo anexados'] : []);
  const jaEnviada = SENT_PROPOSAL_STATUSES.has(proposal?.status);
  const prazoEncerrado = isDeadlinePassed(item);
  const rodando = isJobRunning(latestJob);
  // O portal marca por item "Sem benefícios ME/EPP" (tratamento diferenciado do
  // Art. 4º). É informativo — não tem relação com a declaração ME/EPP abaixo.
  const temItemSemBeneficioMeepp = (ws.items || []).some(
    (it: any) => it.tipoBeneficio === 0 || /sem benef/i.test(String(it.tratamentoDiferenciado || '')),
  ) || /sem benef/i.test(String(item?.tratamentoDiferenciado || ''));

  const decl = (id: string) => declarations.find((d) => d.id === id)?.currentValue;
  const setDecl = (id: string, label: string, value: string) => {
    const next = [...declarations];
    const i = next.findIndex((d) => d.id === id);
    if (i >= 0) next[i] = { ...next[i], currentValue: value };
    else next.push({ id, label, currentValue: value });
    setDeclarations(next);
  };

  const anexar = async (row: any, file: File) => {
    if (!proposal?.id) return;
    setUploading(row.key);
    try {
      await procurementService.uploadBllDocument(proposal.id, row.exigencia, !!row.obrigatorio, file);
      actions.showToast(`"${row.exigencia}" anexado`, 'success');
      await ws.reload();
      await ws.reloadChecklist();
    } catch (e: any) {
      actions.showToast('Erro ao anexar: ' + (e?.response?.data?.message || e?.message), 'error');
    } finally {
      setUploading(null);
    }
  };

  const verDoc = async (docId: number) => {
    try {
      const { url } = await procurementService.getGeneratedDocUrl(docId);
      window.open(url, '_blank');
    } catch {
      actions.showToast('Erro ao abrir o PDF', 'error');
    }
  };

  const radio = (label: string, value: string, current: string | undefined, onPick: (v: string) => void, opts: string[]) => (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-xs text-slate-300">{label}</span>
      <div className="flex items-center gap-1 shrink-0">
        {opts.map((o) => (
          <button
            key={o}
            type="button"
            onClick={() => onPick(o)}
            className={`px-2.5 py-1 rounded text-[11px] transition-colors ${current === o ? 'bg-central-elevado text-white font-semibold border border-central-borda' : 'bg-central-cartao text-slate-300 hover:bg-central-elevado border border-central-borda'}`}
          >{o}</button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* 1. CHECAGEM PRÉ-ENVIO */}
      <div className={`${SURFACE_CENTRAL.card} p-4 space-y-2`}>
        <div className="flex items-center gap-2 text-sm text-slate-200 font-medium">
          <Icon name="check" className="w-4 h-4 text-slate-400" />
          Checagem antes de enviar
        </div>
        {checagem.estado === 'verde' ? (
          <div className={`text-xs px-3 py-2 rounded-lg ${TONE_BADGE.success}`} data-checagem="verde">
            ✓ Conferido no HUB: {conferido.join(', ')}. Isso não diz se o portal vai aceitar.
          </div>
        ) : checagem.estado === 'atencao' && checagem.tentativa ? (
          // ⛔ Não é "tudo certo": o HUB sabe que o último envio não passou. A mensagem do portal vai junto.
          <div className={`text-xs px-3 py-2 rounded-lg border border-amber-600/50 ${TONE_BADGE.warning}`} data-checagem="atencao">
            <div>
              ⚠️ As conferências do HUB passaram ({conferido.join(', ')}), mas{' '}
              {checagem.tentativa.status === 'needs_manual'
                ? 'o último envio parou pedindo ação manual'
                : 'o último envio foi recusado pelo portal'}
              {checagem.tentativa.quando ? ` em ${fmtDateTime(checagem.tentativa.quando)}` : ''}:
            </div>
            <div className="mt-1 text-amber-200/90 italic" data-checagem-motivo>
              “{checagem.tentativa.mensagem.slice(0, 400)}”
            </div>
          </div>
        ) : checagem.estado === 'atencao' && checagem.historicoIndisponivel ? (
          // ⛔ O histórico de envios não carregou: sem ele, "nenhuma tentativa" pode ser só a lista que não chegou.
          <div className={`text-xs px-3 py-2 rounded-lg border border-amber-600/50 ${TONE_BADGE.warning}`} data-checagem="atencao">
            ⚠️ As conferências do HUB passaram ({conferido.join(', ')}), mas o histórico de envios não carregou
            ({checagem.historicoIndisponivel}): não dá para saber se a última tentativa foi recusada pelo portal.
          </div>
        ) : checagem.estado === 'conferindo' ? (
          <div className="text-xs px-3 py-2 rounded-lg bg-central-cartao text-slate-400" data-checagem="conferindo">
            Conferindo o histórico de envios…
          </div>
        ) : (
          <ul className="space-y-1">
            {check.reasons.map((r) => (
              <li key={r} className="text-xs text-amber-300 flex items-start gap-2">
                <span className="mt-0.5">⚠️</span><span>{r}</span>
              </li>
            ))}
          </ul>
        )}
        {/* A nota antiga ("o checklist não bloqueia, é orientação") virou mentira
            quando documento obrigatório passou a impedir o envio. Documento
            OPCIONAL do processo segue sem bloquear — é essa a distinção. */}
        <div className="text-[10px] text-slate-500">
          {docsCheck
            ? 'Documento obrigatório do processo impede o envio; os opcionais, não.'
            : 'Este portal não exige documento anexado no cadastro da proposta.'}
        </div>
      </div>

      {/* 2. TERMOS DO PORTAL (só os do portal desta licitação) */}
      <div className={`${SURFACE_CENTRAL.card} p-4 space-y-2`}>
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-200 font-medium">
            Termos e declarações do portal
            {portal && <span className="text-slate-500 font-normal text-xs ml-2">{portal.toUpperCase()}</span>}
          </div>
        </div>

        {/* Ordem e vocabulário do próprio ComprasNet (bloco "Termo/declarações"
            do cadastro da proposta): Termo de Aceitação → ME/EPP → Art. 60. */}
        {td && (
          <div className="py-1.5">
            <div className="flex items-start gap-2">
              <label className="flex items-start gap-2 cursor-pointer flex-1 min-w-0">
                <input
                  type="checkbox"
                  checked={ws.termsAccepted}
                  onChange={(e) => ws.setTermsAccepted(e.target.checked)}
                  className="mt-0.5 rounded border-slate-500 bg-slate-700"
                />
                <span className="text-xs text-slate-300">
                  {td.termoAceitacao?.label || 'Termo de Aceitação'}
                  <span className="block text-[10px] text-slate-500">
                    {totalDeclaracoes > 0
                      ? `Aceito todas as ${totalDeclaracoes} declarações que o portal engloba neste termo.`
                      : 'Declaro que cumpro e estou ciente de todas as declarações contidas no termo de aceitação.'}
                  </span>
                </span>
              </label>
              {totalDeclaracoes > 0 && (
                <button
                  type="button"
                  onClick={() => setShowTermsDetails(!showTermsDetails)}
                  className="shrink-0 text-[11px] text-slate-300 underline hover:text-white"
                >
                  {showTermsDetails ? 'Ocultar' : 'Ver as declarações'} ▾
                </button>
              )}
            </div>
            {showTermsDetails && (
              <div className="mt-2 pl-6 space-y-2">
                {declGrupos.map(([grupo, decls]) => (
                  <div key={grupo}>
                    <div className="text-[10px] uppercase font-medium text-slate-400 mb-1">
                      {grupo.replace(/_/g, ' ')}
                    </div>
                    <ul className="space-y-1">
                      {decls.map((d, i) => (
                        <li key={i} className="text-[11px] text-slate-300 leading-snug">• {d}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
            {totalDeclaracoes === 0 && (
              <div className="mt-1 text-[10px] text-amber-400/80">
                O portal não devolveu o texto das declarações neste item — sincronize para trazê-las.
              </div>
            )}
          </div>
        )}

        {/* Declaração ME/EPP — é ESTA que garante o tratamento favorecido da
            LC 123/2006. NÃO confundir com o "Sem benefícios ME/EPP" que o
            portal exibe nos itens: aquilo é informativo (Art. 4º da 14.133,
            diz que o ITEM não tem cota/benefício) e não muda esta resposta. */}
        <div className="py-1.5">
          {radio('Declaração para fornecedores ME/EPP e equiparados', 'meepp', ws.meeppValue || undefined, (v) => ws.setMeeppValue(v as any), ['Sim', 'Não'])}
          <div className="text-[10px] text-slate-500 -mt-1">
            Declaro que não ultrapassei o limite de faturamento e cumpro os requisitos da LC 123/2006, apto ao tratamento favorecido dos artigos 42 a 49.
            {temItemSemBeneficioMeepp && (
              <span className="block text-slate-600 mt-0.5">
                O aviso "Sem benefícios ME/EPP" nos itens é informativo (Art. 4º) e não altera esta declaração.
              </span>
            )}
          </div>
        </div>

        {td && (
          <>

            {td.art60 && (
              <div className="border-t border-central-borda pt-2">
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                  Critérios de desempate — Art. 60, Lei 14.133/2021
                </div>
                {radio('Equidade de gênero (inciso III)', 'eq', decl('art60_equidade'), (v) => setDecl('art60_equidade', 'Equidade de genero (Art. 60, inciso III)', v), ['Sim', 'Não'])}
                {decl('art60_equidade') === 'Sim' &&
                  radio('Nível de enquadramento', 'niv', decl('art60_enquadramento'), (v) => setDecl('art60_enquadramento', 'Nivel de enquadramento', v), ['Ouro', 'Prata', 'Bronze'])}
                {radio('Programa de integridade (inciso IV)', 'int', decl('art60_integridade'), (v) => setDecl('art60_integridade', 'Programa de integridade (Art. 60, inciso IV)', v), ['Sim', 'Não'])}
              </div>
            )}

            {/* Margem de preferência (CICS): SÓ quando o portal marcou itens com
                utilizaMargemPreferencia. Mostrar sempre induzia a preencher algo
                que aquele pregão não pede. */}
            {td.margemPreferenciaItens?.length > 0 && (
            <div className="border-t border-central-borda pt-2">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                Margem de preferência (CICS)
                <span className="normal-case tracking-normal text-slate-600 ml-2">
                  exigida no(s) item(ns) {td.margemPreferenciaItens.join(', ')}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-slate-500 block mb-0.5">País de origem</label>
                  <input
                    value={ws.margemPaisOrigem}
                    onChange={(e) => ws.setMargemPaisOrigem(e.target.value)}
                    className={`w-full rounded px-2 py-1 text-xs outline-none ${campo}`}
                    placeholder="CHN"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 block mb-0.5">Enquadramento</label>
                  <select
                    value={ws.margemEnquadramento}
                    onChange={(e) => ws.setMargemEnquadramento(e.target.value)}
                    className={`w-full rounded px-2 py-1 text-xs outline-none ${campo}`}
                  >
                    <option value="0">Não se aplica</option>
                    <option value="1">Aplica margem de preferência</option>
                  </select>
                </div>
              </div>
            </div>
            )}
          </>
        )}

        {/* PCP: informações complementares (desempate) */}
        {portal === 'pcp' && ws.pcpInfoComp.length > 0 && (
          <div className="border-t border-central-borda pt-2 space-y-1">
            <div className="text-[10px] uppercase tracking-wide text-slate-500">Informações complementares (PCP)</div>
            {ws.pcpInfoComp.map((ic, i) => (
              <div key={ic.field} className="flex items-center justify-between gap-2 py-1">
                <span className="text-xs text-slate-300">{ic.label}</span>
                <div className="flex gap-1">
                  {['Sim', 'Não'].map((o) => (
                    <button
                      key={o}
                      type="button"
                      onClick={() => {
                        const next = [...ws.pcpInfoComp];
                        next[i] = { ...next[i], resposta: o };
                        ws.setPcpInfoComp(next);
                      }}
                      className={`px-2 py-0.5 rounded text-[11px] ${ic.resposta === o ? 'bg-central-elevado text-white font-semibold border border-central-borda' : 'bg-central-cartao text-slate-300 hover:bg-central-elevado border border-central-borda'}`}
                    >{o}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {!td && portal !== 'pcp' && (
          <div className="text-[11px] text-slate-500">
            Este portal não exigiu termos adicionais (ou o scrape ainda não os capturou).
          </div>
        )}
      </div>

      {/* 3. DOCUMENTOS DO PROCESSO — o MESMO card do estágio Documentos, lendo
             a mesma fonte. Aqui existiam duas listas divergentes: esta contava
             "0/2 anexados" enquanto o envio já dava o slot da proposta por
             atendido pela Proposta Financeira assinada. */}
      {checklist?.exigeDocumentos === true && (
        <ProcessDocumentsCard
          checklist={checklist}
          loading={checklistLoading}
          templates={[]}
          onReload={() => ws.reloadChecklist()}
          onLinkTemplate={() => actions.showToast('Vincule a declaração no estágio Documentos', 'error')}
          onToggleOverride={async (row, status) => {
            if (!proposal?.id) return;
            const jaTem = row.override?.status === status;
            try {
              await procurementService.setChecklistOverride(proposal.id, { key: row.key, status: jaTem ? null : status });
              await ws.reloadChecklist();
            } catch {
              actions.showToast('Erro ao marcar exigência', 'error');
            }
          }}
          onGenerate={() => actions.showToast('Gere a declaração no estágio Documentos', 'error')}
          onViewDoc={verDoc}
          onUpload={anexar}
          uploadingKey={uploading}
        />
      )}

      {/* 4. ENVIAR. No modo que só GRAVA ITENS (Licitanet), antes de enviada, o bloco da gravação toma o lugar do envio. */}
      {/* Enviada PELO HUB (o portal aceitou o envio): o bloco segue, com o placar item a item e a resposta do portal. */}
      {gravaItens && gravacao && (!jaEnviada || propostaEnviadaPeloHub(gravacao.job)) && <GravarItensNoPortal g={gravacao} onJaEnviei={actions.onMarkSent} linkDoPortal={item?.linkOrigem} />}
      {!(gravaItens && !jaEnviada && gravacao) && (
      <div className={`${SURFACE_CENTRAL.card} p-4 space-y-3`}>
        <div className="text-sm text-slate-200 font-medium">Envio ao portal</div>
        {jaEnviada && (
          <div className="text-[11px] text-slate-400">
            Já enviada {proposal?.submittedVia?.startsWith('robo') ? 'pelo robô' : proposal?.submittedVia === 'manual_flip' ? '(marcada manualmente)' : ''}.
            {!semEnvio && ' Reenviar substitui o cadastro no portal.'}
          </div>
        )}
        {semEnvio && !jaEnviada && (
          <div data-sem-envio-pelo-hub className="text-xs text-slate-300">{FRASE_SEM_ENVIO_PELO_HUB}</div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          {!semEnvio && (
            <button
              onClick={async () => { if (ws.dirty) await ws.save({ silent: true }); actions.onOpenSubmit(); }}
              disabled={!check.ok}
              title={check.ok ? undefined : check.reasons[0]}
              className="px-4 py-2 bg-primary hover:bg-primary-strong disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
            >
              {jaEnviada ? 'Reenviar ao portal' : 'Enviar proposta'}
            </button>
          )}
          {/* Portais sem integração de envio (Licitanet, Licitar Digital) só têm este
              caminho: sem ele a proposta fica presa em rascunho para sempre e
              o estágio de Disputa não abre. Para eles, é a ação principal. */}
          {!jaEnviada && (
            <button
              onClick={actions.onMarkSent}
              data-ja-enviei-manualmente
              title="Registra no Hub que a proposta já foi cadastrada à mão no portal. Não envia nada."
              className={semEnvio
                ? 'px-4 py-2 bg-primary hover:bg-primary-strong text-white rounded-lg text-sm font-medium transition-colors'
                : 'px-3 py-2 bg-central-cartao border border-central-borda hover:bg-central-elevado text-slate-200 rounded-lg text-xs transition-colors'}
            >
              Já enviei manualmente
            </button>
          )}
          {jaEnviada && (
            <button
              onClick={actions.onWithdraw}
              disabled={prazoEncerrado || rodando}
              title={
                prazoEncerrado
                  ? 'A licitação já encerrou o recebimento — o portal não abre mais o cadastro de propostas, então a desistência não é possível por aqui.'
                  : rodando ? 'Aguarde a automação em andamento' : undefined
              }
              className="px-3 py-2 bg-central-cartao border border-central-borda hover:bg-central-elevado disabled:opacity-40 disabled:cursor-not-allowed text-slate-200 rounded-lg text-xs transition-colors"
            >
              Desistir da compra
            </button>
          )}
          {jaEnviada && prazoEncerrado && (
            <span className="text-[11px] text-slate-500">
              Prazo encerrado: não dá mais para desistir nem reenviar por aqui.
            </span>
          )}
        </div>

        {jobs.length > 0 && (
          <div className="pt-1">
            <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Histórico de automações</div>
            <div className="space-y-1">
              {jobs.slice(0, 5).map((j) => (
                <button
                  key={j.id}
                  onClick={() => actions.onViewJob(j)}
                  className="w-full text-left bg-central-elevado hover:bg-central-elevado rounded px-2.5 py-1.5 transition-colors"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] text-slate-300">
                      {j.action === 'scrape_proposal' ? 'Sincronização'
                        : String(j.action || '').startsWith('withdraw') ? 'Desistência' : 'Envio'}
                      {ehSimulacao(j) && <span className="text-slate-500"> (teste)</span>}
                    </span>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${JOB_BADGE[j.status ?? ''] || TONE_BADGE.neutral}`}>
                        {JOB_LABEL[j.status ?? ''] || j.status}
                      </span>
                      <span className="text-[10px] text-slate-500">{quandoDoJob(j) ? fmtDateTime(quandoDoJob(j)) : ''}</span>
                    </div>
                  </div>
                  {j.errorMessage && (
                    <div className="text-[10px] text-red-400/90 mt-0.5 leading-snug">{String(j.errorMessage).slice(0, 160)}</div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
